import '@googleworkspace/drive-picker-element';
import { enableProdMode } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import * as Sentry from '@sentry/angular';
import { AppComponent } from './app/app.component';
import { appConfig } from './app/app.config';
import { configureJeptoLegacyHighcharts } from './app/shared/utils/jepto-legacy-chart-surface.util';
import { configureGridPro } from '@framework/analytics/grid-pro.config';
import { environment } from './environments/environment';
import { configureJeptoUserPoolAmplify } from './app/shared/utils/amplify-bootstrap.util';
import { log } from '../src/app/shared/utils/logger';
import {
	applySentryBeforeSend,
	SENTRY_DENY_URLS,
} from './app/shared/utils/sentry-before-send.util';
import {
	readBrowserAutomationSignals,
	shouldEnableSentryReplay,
} from './app/shared/utils/sentry-replay-guard.util';
import { DatachatToolResultComponent } from './app/features/datachat/components/datachat-tool-result.component';

configureJeptoLegacyHighcharts();
configureGridPro({ gridKey: environment.highchartsGridKey });

const sentryConfig = environment.sentry;

if (sentryConfig?.enabled && sentryConfig?.dsn) {
	const tracePropagationTargets = [
		'localhost',
		environment.appSyncConfig.graphqlEndpoint,
		environment.appSyncConfig.customEndpoint,
		environment.appSyncConfig.eventsEndpoint,
		environment.datachatAPI.apiURL,
		environment.apiBaseURL
			? new RegExp(
					`^${environment.apiBaseURL.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?!/media)`
				)
			: null,
	].filter(Boolean);

	const sentryEnvironment = environment.production
		? 'production'
		: environment.staging
			? 'staging'
			: 'development';

	const maskAllInputs = false;
	const sendDefaultPii = true;
	const enableReplay = shouldEnableSentryReplay(
		readBrowserAutomationSignals()
	);

	Sentry.init({
		dsn: sentryConfig.dsn,
		release: environment.sentryRelease,
		sendDefaultPii,
		denyUrls: [...SENTRY_DENY_URLS],
		// Drop expected Amplify/auth noise; fingerprint Zone-wrapped errors. See JEP-9679.
		beforeSend(event, hint) {
			try {
				return applySentryBeforeSend(event, hint);
			} catch {
				return event;
			}
		},
		integrations: [
			Sentry.browserTracingIntegration(),
			...(enableReplay
				? [
						Sentry.replayIntegration({
							maskAllText: false,
							maskAllInputs,
							mask: ['input[type="password"]'],
							blockAllMedia: false,
							networkDetailAllowUrls: tracePropagationTargets,
							networkCaptureBodies: true,
						}),
					]
				: []),
		],
		tracesSampleRate: sentryConfig.tracesSampleRate,
		tracePropagationTargets,
		replaysSessionSampleRate: enableReplay
			? sentryConfig.replaysSessionSampleRate
			: 0,
		replaysOnErrorSampleRate: enableReplay
			? sentryConfig.replaysOnErrorSampleRate
			: 0,
		enableLogs: true,
		environment: sentryEnvironment,
		debug: sentryConfig.debug ?? false,
	});
}

configureJeptoUserPoolAmplify(environment);

if (!customElements.get('jepto-tool-result')) {
	customElements.define('jepto-tool-result', DatachatToolResultComponent);
	log('[Main] Registered jepto-tool-result custom element');
}

if (environment.production) {
	enableProdMode();
	if (window) window.console.log = function () {};
}

(window as any).ShadowRoot = class extends DocumentFragment {};

document.addEventListener('DOMContentLoaded', () => {
	// Sentry user context (APP_INITIALIZER) and the service worker are
	// registered by app.config.ts providers.
	bootstrapApplication(AppComponent, appConfig).catch(error => {
		console.error(error);
	});
});
